import {
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  IconButton,
  Collapse,
  Badge
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Lock, LockOpen, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { formatDate } from '../../resources/functions';
import TableCell from '../TableCell';
import Title from '../Title';
import { ScootersLockHistoryTableProps } from './ScootersLockHistoryTableTypes';
import { getScooterColor } from '../../helpers/scooter-markers-helper';
import { ScooterStatus, ScooterColorKeys } from '../../pages/ZonesPage/interface';

// Функція для отримання тексту та кольору статусу
const getStatusDisplay = (status: string, theme: any, t: TFunction) => {
  let colorKey: ScooterColorKeys;
  let label: string;
  
  switch (status) {
    case 'AVAILABLE':
      colorKey = 'free';
      label = t('lockHistory.statusActiveForRent');
      break;
    case 'CHARGING':
      colorKey = ScooterStatus.CHARGING;
      label = t('lockHistory.statusOnCharge');
      break;
    case 'TRANSPORTING':
      colorKey = ScooterStatus.TRANSPORTING;
      label = t('lockHistory.statusTransport');
      break;
    case 'MAINTENANCE':
      colorKey = ScooterStatus.MAINTENANCE;
      label = t('lockHistory.statusInRepair');
      break;
    case 'BROKEN_IN_CITY':
      colorKey = ScooterStatus.BROKEN_IN_CITY;
      label = t('lockHistory.statusBrokenInCity');
      break;
    case 'LOW_BATTERY':
      colorKey = 'discharged';
      label = t('lockHistory.statusLowBattery');
      break;
    case 'HIDDEN':
      colorKey = ScooterStatus.HIDDEN;
      label = t('lockHistory.statusHidden');
      break;
    case 'BLOCKED':
      colorKey = 'all';
      label = t('lockHistory.statusBlocked');
      break;
    case 'UNLOCKED':
      colorKey = 'unlocked';
      label = t('lockHistory.statusUnlocked');
      break;
    default:
      colorKey = 'all';
      label = status;
  }
  
  const bgcolor = getScooterColor(colorKey);
  const color = getIconColor(bgcolor);
  
  return { label, color, bgcolor };
};

const getIconColor = (backgroundColor: string): string => {
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

const ScootersLockHistoryTable = ({
  isLocked,
  rows,
  isScooterId
}: ScootersLockHistoryTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  // Функція для отримання статусу з рядка
  const getRowStatus = (row: any) => {
    if (!row?.newStatus || row.newStatus.length === 0) {
      return row.lock ? 'BLOCKED' : 'UNLOCKED';
    }
    return row.newStatus;
  };

  // Групуємо послідовні рядки з однаковим самокатом та дією
  const groupedRows = rows.reduce((acc: any[], row, index) => {
    const currentScooterId = row.scooterId?._id;
    const currentStatus = getRowStatus(row);
    
    if (index === 0) {
      acc.push({
        isGroup: false,
        rows: [row],
        scooterId: currentScooterId,
        status: currentStatus
      });
    } else {
      const lastGroup = acc[acc.length - 1];
      const prevScooterId = lastGroup.scooterId;
      const prevStatus = lastGroup.status;
      
      // Якщо самокат і статус такі самі - додаємо до групи
      if (currentScooterId === prevScooterId && currentStatus === prevStatus) {
        lastGroup.rows.push(row);
        lastGroup.isGroup = lastGroup.rows.length > 1;
      } else {
        // Інакше створюємо нову групу
        acc.push({
          isGroup: false,
          rows: [row],
          scooterId: currentScooterId,
          status: currentStatus
        });
      }
    }
    
    return acc;
  }, []);

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupIndex)) {
        newSet.delete(groupIndex);
      } else {
        newSet.add(groupIndex);
      }
      return newSet;
    });
  };

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLocked ? (
            <Lock sx={{ color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' }} />
          ) : (
            <LockOpen sx={{ color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' }} />
          )}
          <Title variant='h5'>{isLocked ? t('lockHistory.locked') : t('lockHistory.unlocked')}</Title>
        </Box>
      </Grid>
      <Grid item sx={{ width: '100%' }}>
        <TableContainer sx={{ width: '100%' }}>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: '20%'
                  }}
                >
                  {t('lockHistory.colScooter')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: '25%'
                  }}
                >
                  {t('lockHistory.colTime')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: isScooterId ? '55%' : '30%'
                  }}
                >
                  {t('lockHistory.colStatus')}
                </TableCell>
                {!isScooterId && (
                  <TableCell 
                    sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                      padding: '10px 16px',
                      fontWeight: 600,
                      borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                      width: '25%'
                    }}
                  >
                    {t('lockHistory.colUser')}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.map((group, groupIndex) => {
                const isExpanded = expandedGroups.has(groupIndex);
                const firstRow = group.rows[0];
                const displayRows = group.isGroup && !isExpanded ? [firstRow] : group.rows;
                
                return displayRows.map((row: any, rowIndex: number) => {
                  const isFirstInGroup = rowIndex === 0;
                  const status = getRowStatus(row);
                  const statusDisplay = getStatusDisplay(status, theme, t);
                  
                  return (
                    <TableRow key={row._id}>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isLocked ? (
                            <Lock 
                              sx={{ 
                                fontSize: 16,
                                color: theme.palette.mode === 'dark' ? '#5073F8' : '#1976D2' 
                              }} 
                            />
                          ) : (
                            <LockOpen 
                              sx={{ 
                                fontSize: 16,
                                color: theme.palette.mode === 'dark' ? '#5073F8' : '#4CAF50' 
                              }} 
                            />
                          )}
                          <span>{row.scooterId?.name}</span>
                          {group.isGroup && isFirstInGroup && (
                            <IconButton
                              size="small"
                              onClick={() => toggleGroup(groupIndex)}
                              sx={{ ml: 1, p: 0.5 }}
                            >
                              <Badge 
                                badgeContent={group.rows.length} 
                                color="primary"
                                sx={{
                                  '& .MuiBadge-badge': {
                                    backgroundColor: '#FFE082',
                                    color: '#000000'
                                  }
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLess sx={{ fontSize: 20 }} />
                                ) : (
                                  <ExpandMore sx={{ fontSize: 20 }} />
                                )}
                              </Badge>
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        {formatDate(row.createdAt, { time: true })}
                      </TableCell>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        <Chip
                          label={statusDisplay.label}
                          size='small'
                          sx={{
                            bgcolor: statusDisplay.bgcolor,
                            color: statusDisplay.color,
                            fontWeight: 500,
                            minWidth: 150
                          }}
                        />
                      </TableCell>
                      {!isScooterId && (
                        <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                          {row?.adminId ? row?.adminId?.login ?? row.type : ''}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default ScootersLockHistoryTable;
